/* eslint no-await-in-loop: 0 no-restricted-syntax: 0 */
import React, { ReactElement, useEffect, useState } from 'react';

import gql from 'graphql-tag';
import { useMutation } from 'react-apollo';
import ReactGA from 'react-ga4';

import * as actions from 'actions';
import { useI18n } from 'Hooks';
import store from 'store';

import errorMapper from './errorMapper';
import LoginView from './view';

const LOGIN_MUTATION = gql`
  mutation login($username: String, $password: String!) {
    login(username: $username, password: $password) {
      error
      token
      message
    }
  }
`;

interface Props {
  onRedirectEnabled: (redirectEnabled: boolean) => void;
}

// Company IDs from the old "companyId+phone number" login method
// const ids: { [key: string]: string[] } = {
//   default: [
//     'username',
//     'cjfy7vfsy36g901468wekic9z',
//     'cjxeikrya8scb0162fr5rbfo3',
//     'cjcuqi7a8040g0118o5zbdg5g',
//     'cjw6zwz7i2lwk0185z79yx48i',
//     'cjnvltbmce5v101675223ibj5',
//     'cjnvlwhvee5aj0183ceuaxmq1',
//   ],
//   'https://djpqty2rsb.execute-api.eu-north-1.amazonaws.com/prod/graphqlbae-mingo-production':
//     [
//       'username',
//       'cjiwcnx36srn00183bbhts4g7',
//       'ck2xhvggr4vih0109zwxdjkrl',
//       'cjofxv7vfiqrh0183j02bh4za',
//       'cjfdt1s6h9lon0172ykiwwebc',
//       'cjpympjfxuppr0167sh0wbqsh',
//       'cjpymfwkuup1s0183ghjhwntc',
//     ],
// };

const Login: React.FunctionComponent<Props> = ({ onRedirectEnabled }) => {
  const { t } = useI18n();
  const [login] = useMutation(LOGIN_MUTATION);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | ReactElement>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Pre-warm the lambda
    login({
      variables: {
        username: 'DUMMY_USERNAME',
        password: 'DUMMY_PASSWORD',
        backoffice: true,
      },
    });
  }, []);

  const handleSetField = (fieldName: string, fieldValue: string) => {
    switch (fieldName) {
      case 'username':
        setUsername(fieldValue);
        break;

      case 'password':
        setPassword(fieldValue);
        break;

      default:
        break;
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError('');

      /** We let backend know that we're trying to login from backoffice (used to deny users with role: REGULAR from logging into backoffice) */
      const IS_BACKOFFICE = true;

      const variables: any = {
        password,
        username,
        backoffice: IS_BACKOFFICE,
      };

      const results = await login({ variables });
      const loginData = results.data.login;
      const { error, message: resMessage, token } = loginData;

      if (token) {
        store.dispatch(actions.setToken(token));

        onRedirectEnabled(true);

        setLoading(false);
        return;
      }

      const message = resMessage ?? errorMapper(error, t);

      setError(message || '');
      setLoading(false);
    } catch (error) {
      //@ts-ignore
      setError(error.message);
      setLoading(false);
    }
  };

  // Trigger GA event that initializing an entering to Login (example).
  ReactGA.event({
    category: 'Login',
    action: 'login_page_opened',
  });

  return (
    <LoginView onSetField={handleSetField} onLogin={handleLogin} error={error} loading={loading} />
  );
};

export default Login;
